import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';
import { SubItem } from './types';

interface SearchSuggestionsProps {
  showSuggestions: boolean;
  searchQuery: string;
  recentSearches: string[];
  filteredSuggestions: SubItem[];
  handleSearchSubmit: (query: string) => void;
  clearRecentSearches: () => void;
  scrollToSection: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => void;
  setShowSuggestions: (show: boolean) => void;
  setSearchQuery: (query: string) => void;
}

export default function SearchSuggestions({
  showSuggestions,
  searchQuery,
  recentSearches,
  filteredSuggestions,
  handleSearchSubmit,
  clearRecentSearches,
  scrollToSection,
  setShowSuggestions,
  setSearchQuery
}: SearchSuggestionsProps) {
  return (
    <AnimatePresence>
      {showSuggestions && (searchQuery.trim() !== '' || recentSearches.length > 0) && (
        <motion.div
          layout
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute top-full mt-2 right-0 w-72 bg-brand-dark/95 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl overflow-hidden z-[60] p-2"
        >
          {searchQuery.trim() === '' && recentSearches.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center justify-between px-3 py-1 mb-1">
                <p className="text-[10px] text-white/40 uppercase tracking-wider font-bold">عمليات البحث الأخيرة</p>
                <button 
                  onClick={clearRecentSearches}
                  className="text-[10px] text-brand-accent hover:text-white transition-colors font-bold"
                >
                  مسح الكل
                </button>
              </div>
              {recentSearches.map((search, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setSearchQuery(search);
                    handleSearchSubmit(search);
                  }}
                  className="w-full text-right px-3 py-2 text-sm text-white/70 hover:text-brand-accent hover:bg-white/5 rounded-lg flex items-center gap-2"
                >
                  <Search size={12} className="opacity-40" />
                  {search}
                </button>
              ))}
            </div>
          )}
          
          {filteredSuggestions.length > 0 ? (
            <div>
              <p className="text-[10px] text-white/40 px-3 py-1 uppercase tracking-wider font-bold">الاقتراحات</p>
              {filteredSuggestions.map((item, i) => (
                <a 
                  href={item.href}
                  key={i}
                  target={item.href.startsWith('http') ? '_blank' : '_self'}
                  rel={item.href.startsWith('http') ? 'noopener noreferrer' : ''}
                  className="w-full text-right px-3 py-2 text-sm text-white/80 hover:text-brand-accent hover:bg-white/5 rounded-lg flex items-center justify-between group"
                  onClick={(e) => {
                    handleSearchSubmit(item.name);
                    setShowSuggestions(false);
                    scrollToSection(e, item.href);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-brand-accent opacity-50 group-hover:opacity-100">
                      {item.icon && <item.icon size={18} />}
                    </span>
                    <span>{item.name}</span>
                  </div>
                  <span className="text-[10px] bg-white/5 text-white/40 px-2 py-0.5 rounded-full">{item.type}</span>
                </a>
              ))}
            </div>
          ) : searchQuery.trim() !== '' && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-white/40">لا توجد نتائج مطابقة</p>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
